<template>
    <div class="ppv2-relative ppv2-w-full">
        <div v-if="isLoading" class="ppv2-flex ppv2-justify-center ppv2-items-center ppv2-py-24">
            <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>

        <template v-else>
            <div class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-px-1 ppv2-pb-1">
                <span
                    class="poppinsRegular ppv2-uppercase ppv2-tracking-wider"
                    :style="{ fontSize: normalizeFontSize(11) }"
                    style="color: #9aa4b2"
                >
                    {{ $t('forms.questionsHeader.order') }}
                </span>
                <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(12.5) }" style="color: #6b7480">
                    {{ requiredSummary }}
                </span>
            </div>

            <div class="ppv2-flex ppv2-flex-col ppv2-gap-2.5">
                <template v-for="(question, i) in questions">
                    <div
                        :key="question.uid"
                        class="ppv2-bg-white ppv2-rounded-xl ppv2-border ppv2-border-gray-200 ppv2-overflow-hidden ppv2-transition-all"
                        :class="
                            expandedUid === question.uid ? '!ppv2-border-[#7D2DBD]/40 !ppv2-shadow-sm' : 'hover:ppv2-shadow-sm'
                        "
                    >
                        <FormQuestionRow
                            :question="question"
                            :expanded="expandedUid === question.uid"
                            :index="i"
                            :is-first="i === 0"
                            :is-last="i === questions.length - 1"
                            @toggle="toggleExpand(question.uid)"
                            @move="(dir) => moveQuestion(i, dir)"
                            @duplicate="duplicateQuestion(question)"
                            @delete="requestDelete(question)"
                        />
                        <FormQuestionEditor
                            v-if="expandedUid === question.uid"
                            :key="`${question.uid}-editor`"
                            :question="question"
                            :token="token"
                            @close="expandedUid = null"
                            @delete="requestDelete(question)"
                        />
                    </div>
                </template>

                <FormTypePicker v-if="showTypePicker" @select="addQuestion" @close="showTypePicker = false" />

                <button
                    v-else
                    type="button"
                    class="ppv2-w-full ppv2-h-[48px] ppv2-mt-1 ppv2-rounded-xl ppv2-border-[1.5px] ppv2-border-dashed ppv2-border-gray-300 ppv2-text-gray-500 hover:ppv2-border-[#7D2DBD] hover:ppv2-text-[#7D2DBD] hover:ppv2-bg-[#7d2dbd] hover:ppv2-bg-opacity-10 ppv2-transition-colors ppv2-flex ppv2-items-center ppv2-justify-start ppv2-px-3.5 ppv2-gap-2 poppinsRegular"
                    :style="{ fontSize: normalizeFontSize(13.5) }"
                    @click="showTypePicker = true"
                >
                    <FormTypeIcon name="plus" :size="16" />
                    {{ $t('forms.question.add') }}
                </button>
            </div>

            <FormPresentation
                class="ppv2-mt-6"
                :description="description"
                :image="image"
                @update:description="description = $event"
                @update:image="image = $event"
            />

            <FormFAQEditor
                class="ppv2-mt-6"
                :faqs="faqs"
                @add="addFaq"
                @remove="removeFaq"
                @move="moveFaq"
                @propose="proposeFaq"
            />

            <div class="ppv2-pt-6 ppv2-pb-[104px]"></div>

            <button
                type="button"
                class="ppv2-fixed ppv2-z-[15] ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-2 poppinsSemiBold"
                :style="{
                    right: '24px',
                    bottom: '20px',
                    height: '46px',
                    padding: '0 26px',
                    borderRadius: '11px',
                    border: 'none',
                    background: '#7d2dbd',
                    color: '#fff',
                    fontSize: normalizeFontSize(14),
                    cursor: 'pointer',
                }"
                @click="saveForm"
            >
                <span v-if="isSaving" class="spinner-border spinner-border-sm"></span>
                {{ isSaving ? $t('forms.save.saving') : $t('forms.save.label') }}
            </button>

            <div
                v-if="deleteCandidate"
                class="ppv2-fixed ppv2-inset-0 ppv2-z-[100] ppv2-flex ppv2-items-center ppv2-justify-center"
            >
                <div class="ppv2-absolute ppv2-inset-0 ppv2-bg-black/40" @click="deleteCandidate = null"></div>
                <div
                    class="ppv2-relative ppv2-w-[420px] ppv2-max-w-[90vw] ppv2-bg-white ppv2-rounded-2xl ppv2-shadow-xl ppv2-p-6"
                >
                    <div class="ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-mb-2">
                        <div
                            class="ppv2-w-10 ppv2-h-10 ppv2-rounded-full ppv2-bg-red-50 ppv2-flex ppv2-items-center ppv2-justify-center"
                            style="color: #e2483d"
                        >
                            <FormTypeIcon name="trash" :size="18" />
                        </div>
                        <span class="poppinsSemiBold ppv2-text-gray-900" :style="{ fontSize: normalizeFontSize(16) }">
                            {{ $t('forms.question.deleteConfirmTitle') }}
                        </span>
                    </div>
                    <p class="poppinsRegular ppv2-text-gray-500 ppv2-mt-2" :style="{ fontSize: normalizeFontSize(13) }">
                        {{ $t('forms.question.deleteConfirmMessage') }}
                    </p>
                    <div class="ppv2-flex ppv2-justify-end ppv2-gap-2 ppv2-mt-5">
                        <BaseButton
                            type="button"
                            variant="outline"
                            :custom-class="'!ppv2-min-h-[38px] !ppv2-min-w-[100px] !ppv2-text-sm'"
                            @click="deleteCandidate = null"
                        >
                            {{ $t('forms.question.cancel') }}
                        </BaseButton>
                        <BaseButton
                            type="button"
                            variant="danger"
                            :custom-class="'!ppv2-min-h-[38px] !ppv2-min-w-[120px] !ppv2-text-sm'"
                            @click="confirmDelete"
                        >
                            {{ $t('forms.question.delete') }}
                        </BaseButton>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import axios from 'axios'
import BaseButton from '../../Shared/BaseButton.vue'
import FormQuestionRow from './components/FormQuestionRow.vue'
import FormQuestionEditor from './components/FormQuestionEditor.vue'
import FormTypePicker from './components/FormTypePicker.vue'
import FormTypeIcon from './components/FormTypeIcon.vue'
import FormPresentation from './components/FormPresentation.vue'
import FormFAQEditor from './components/FormFAQEditor.vue'
import { FORM_TYPES, typeKeyFromDbValue } from './formTypes'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import { plural } from '../../../utils/plural'

let uidCounter = 0
function makeUid() {
    uidCounter += 1
    return `${Date.now().toString(36)}-${uidCounter}-${Math.random().toString(36).slice(2, 7)}`
}

export default {
    name: 'FormBuilder',
    components: {
        BaseButton,
        FormQuestionRow,
        FormQuestionEditor,
        FormTypePicker,
        FormTypeIcon,
        FormPresentation,
        FormFAQEditor,
    },
    props: {
        slug: {
            type: String,
            required: true,
        },
        formName: {
            type: String,
            default: '',
        },
        token: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            isLoading: false,
            isSaving: false,
            description: '',
            image: null,
            faqs: [],
            questions: [],
            expandedUid: null,
            showTypePicker: false,
            deleteCandidate: null,
            loaded: false,
        }
    },
    computed: {
        requiredSummary() {
            const total = this.questions.length
            const required = this.questions.filter((q) => q.required).length
            if (total === 0) return this.$t('forms.questionsHeader.noneRequired')
            return this.$t('forms.questionsHeader.requiredSummary', {
                n: required,
                plural: plural(required, '', 's'),
                total,
            })
        },
    },
    watch: {
        slug: {
            immediate: true,
            handler() {
                this.fetchForm()
            },
        },
        'questions.length'(count) {
            this.$emit('count-change', count)
        },
    },
    methods: {
        normalizeFontSize,
        async fetchForm() {
            this.isLoading = true
            this.expandedUid = null
            this.showTypePicker = false
            this.deleteCandidate = null
            try {
                const response = await axios.get('/api/v3/formQuestion/getForm', {
                    params: { type: this.slug },
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                if (response.data.success) {
                    const survey = response.data.data.survey
                    this.description = this.normalizeDescription(survey.description_general || '')
                    this.image = response.data.data.image || null
                    this.faqs = (survey.faqs || []).map((faq) => ({
                        question: faq.question,
                        response: faq.answer ?? faq.response,
                    }))
                    this.questions = (survey.questions || []).map((q) => this.normalizeQuestion(q))
                    this.loaded = true
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || error.message)
            } finally {
                this.isLoading = false
            }
        },
        normalizeDescription(html) {
            if (!html) return html
            return html.replace(/<\/h[1-6]>/gi, '</p>').replace(/<h[1-6]([^>]*)>/gi, '<p$1>')
        },
        normalizeQuestion(dbQuestion) {
            const possibilities = Array.isArray(dbQuestion.answers_possibilities)
                ? dbQuestion.answers_possibilities.map((p) => p.possibility)
                : []
            const typeKey = typeKeyFromDbValue(dbQuestion.type)
            const locked = this.isProtectedQuestionName(dbQuestion.question || '')
            const isRequired =
                dbQuestion.is_required === true ||
                dbQuestion.is_required === 1 ||
                dbQuestion.is_required === '1' ||
                dbQuestion.is_required === 'on' ||
                dbQuestion.is_required === 'true'
            return {
                uid: makeUid(),
                id: dbQuestion.id || null,
                label: dbQuestion.question || '',
                placeholder: dbQuestion.placeholder || '',
                typeKey,
                required: locked || isRequired,
                locked,
                help: '',
                min: this.numericOrEmpty(dbQuestion.min_value),
                max: this.numericOrEmpty(dbQuestion.max_value),
                boundsError: false,
                minChoices:
                    typeKey === 'checkbox'
                        ? Number(dbQuestion.numberQuestion_valid) > 1
                            ? Number(dbQuestion.numberQuestion_valid)
                            : 1
                        : 1,
                dateFormat: 'single',
                forbidPast: false,
                options: possibilities,
                numberQuestionValid: dbQuestion.numberQuestion_valid,
            }
        },
        newQuestion(typeKey) {
            return {
                uid: makeUid(),
                id: null,
                label: '',
                placeholder: '',
                typeKey,
                required: false,
                help: '',
                min: '',
                max: '',
                boundsError: false,
                minChoices: 1,
                dateFormat: 'single',
                forbidPast: false,
                options: ['radio', 'checkbox', 'select'].includes(typeKey) ? ['', ''] : typeKey === 'experience' ? [] : [],
                numberQuestionValid: undefined,
            }
        },
        addQuestion(typeKey) {
            const question = this.newQuestion(typeKey)
            this.questions.push(question)
            this.expandedUid = question.uid
            this.showTypePicker = false
        },
        duplicateQuestion(question) {
            if (question.locked) return
            const copy = JSON.parse(JSON.stringify(question))
            copy.uid = makeUid()
            copy.id = null
            copy.numberQuestionValid = undefined
            const index = this.questions.indexOf(question)
            this.questions.splice(index + 1, 0, copy)
            this.expandedUid = copy.uid
            this.showTypePicker = false
        },
        moveQuestion(index, dir) {
            const target = index + dir
            if (target < 0 || target >= this.questions.length) return
            const question = this.questions.splice(index, 1)[0]
            this.questions.splice(target, 0, question)
        },
        requestDelete(question) {
            this.deleteCandidate = question
        },
        confirmDelete() {
            if (this.deleteCandidate && this.deleteCandidate.locked) {
                this.deleteCandidate = null
                return
            }
            const index = this.questions.indexOf(this.deleteCandidate)
            if (index !== -1) this.questions.splice(index, 1)
            if (this.expandedUid === this.deleteCandidate.uid) this.expandedUid = null
            this.deleteCandidate = null
        },
        toggleExpand(uid) {
            this.expandedUid = this.expandedUid === uid ? null : uid
        },
        isProtectedQuestionName(name) {
            if (!name) return false
            const protectedNames = ['Nom', 'E-mail']
            const protectedI18nKeys = ['survey.default_fields.last_name', 'survey.default_fields.email']
            return protectedNames.indexOf(name) !== -1 || protectedI18nKeys.indexOf(name) !== -1
        },
        numericOrEmpty(value) {
            if (value === null || value === undefined || value === '') return ''
            const n = Number(value)
            return Number.isNaN(n) ? '' : n
        },
        addFaq() {
            this.faqs.push({ question: this.$t('forms.faq.newQuestion'), response: '' })
        },
        removeFaq(index) {
            this.faqs.splice(index, 1)
        },
        moveFaq(index, dir) {
            const target = index + dir
            if (target < 0 || target >= this.faqs.length) return
            const [item] = this.faqs.splice(index, 1)
            this.faqs.splice(target, 0, item)
        },
        proposeFaq() {
            this.$toast.info(this.$t('forms.faq.proposeSoon'))
        },
        buildQuestionPayload(question) {
            const possibilities = (question.options || []).map((option) => option.trim()).filter(Boolean)
            let numberQuestionValid = -1
            if (question.typeKey === 'checkbox') {
                numberQuestionValid = parseInt(question.minChoices, 10) || 1
            } else if (typeof question.numberQuestionValid === 'number') {
                numberQuestionValid = question.numberQuestionValid
            }
            let minValue = null
            let maxValue = null
            if (question.typeKey === 'number') {
                minValue = question.min !== '' && question.min !== null && question.min !== undefined ? question.min : null
                maxValue = question.max !== '' && question.max !== null && question.max !== undefined ? question.max : null
            } else if (question.typeKey === 'long') {
                maxValue = question.max !== '' && question.max !== null && question.max !== undefined ? question.max : null
            }
            return {
                id: question.id || null,
                question: question.label,
                placeholder: question.placeholder || null,
                type: FORM_TYPES[question.typeKey].value,
                is_required: question.required ? 'on' : 'off',
                is_deleted: 1,
                numberQuestion_valid: numberQuestionValid,
                min_value: minValue,
                max_value: maxValue,
                question_category_id: 0,
                possibilities,
            }
        },
        hasBoundValue(value) {
            return value !== '' && value !== null && value !== undefined
        },
        hasBoundsError(question) {
            if (question.typeKey === 'number') {
                // min/max are optional for "Nombre" type (backend accepts null for either)
                // Only show error if both are set but inconsistent (min > max)
                const hasMin = this.hasBoundValue(question.min)
                const hasMax = this.hasBoundValue(question.max)
                if (hasMin && hasMax) return Number(question.min) > Number(question.max)
                return false
            }
            if (question.typeKey === 'long') {
                // max_value is now optional for "long" type (backend accepts null)
                // Only show error if max_value is explicitly set but invalid
                if (!this.hasBoundValue(question.max)) return false
                return Number(question.max) < 1
            }
            return false
        },
        buildBoundsErrorMessage(invalidQuestions) {
            const labels = invalidQuestions.map((q) => q.label.trim() || this.$t('forms.question.emptyTitle')).join(', ')
            const hasNumber = invalidQuestions.some((q) => q.typeKey === 'number')
            const hasLong = invalidQuestions.some((q) => q.typeKey === 'long')
            const hasIncoherentMin = invalidQuestions.some(
                (q) =>
                    q.typeKey === 'number' &&
                    this.hasBoundValue(q.min) &&
                    this.hasBoundValue(q.max) &&
                    Number(q.min) > Number(q.max)
            )
            if (hasNumber && hasLong) {
                return this.$t('forms.save.boundsMixedRequired', { labels })
            }
            if (hasNumber) {
                if (hasIncoherentMin) {
                    return this.$t('forms.save.minMaxIncoherent', { labels })
                }
                return this.$t('forms.save.minMaxRequired', { labels })
            }
            return this.$t('forms.save.maxLengthRequired', { labels })
        },
        async saveForm() {
            this.questions.forEach((q) => {
                q.boundsError = false
            })
            const invalidBounds = this.questions.filter(this.hasBoundsError)
            if (invalidBounds.length) {
                invalidBounds.forEach((q) => {
                    q.boundsError = true
                })
                this.expandedUid = invalidBounds[0].uid
                this.$toast.error(this.buildBoundsErrorMessage(invalidBounds))
                return
            }
            this.isSaving = true
            try {
                let imagePayload = this.image
                if (imagePayload instanceof File) {
                    imagePayload = await this.fileToBase64(imagePayload)
                }

                const payload = {
                    slug: this.slug,
                    name: this.formName,
                    description: this.description,
                    image: imagePayload,
                    faqs: JSON.stringify(this.faqs),
                }

                this.questions.forEach((question, index) => {
                    payload[`form-${index}`] = this.buildQuestionPayload(question)
                })

                const response = await axios.post('/api/v3/formQuestion/store', payload, {
                    headers: { Authorization: `Bearer ${this.token}` },
                })

                if (response.data.success) {
                    this.$toast.success(response.data.message || this.$t('forms.save.saved'))
                    await this.fetchForm()
                    this.$emit('saved', { name: this.formName, count: this.questions.length })
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || error.message)
                One?.helpers?.('jq-notify', {
                    type: 'danger',
                    icon: 'fa fa-times me-1',
                    message: error.response?.data?.message || "Erreur lors de l'enregistrement",
                })
            } finally {
                this.isSaving = false
            }
        },
        fileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = () => resolve(reader.result)
                reader.onerror = (error) => reject(error)
                reader.readAsDataURL(file)
            })
        },
    },
}
</script>
